import React from 'react';
import { useForm } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { toastConfiguration } from '../Constant/Data';
import APIRequest from '../services/APIRequest';

const CustomPlanModal = ({ open, onClose }) => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      const response = await APIRequest.post('web/custom-plan-inquiry', data);
      if (response?.ok) {
        toast.success('Your custom plan request has been submitted', toastConfiguration);
        reset();
        onClose();
      } else {
        const message = response?.data?.message || response?.problem || 'Something went wrong. Please try again.';
        toast.error(message, toastConfiguration);
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong. Please try again.', toastConfiguration);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal bg-black/80 h-screen fixed left-0 flex justify-center items-center top-0 w-full z-50 px-6 py-3 md:px-10">
      <div className="bg-[#18202F] max-h-[95vh] p-6 px-4 md:px-8 rounded-lg max-w-xl w-full my-auto md:mx-auto border border-[#4A5567] overflow-y-auto relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Custom Plan Request</h2>
          <p className="text-gray-400 text-sm">Tell us your requirements and we will get back to you.</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex flex-col">
            <label htmlFor="name" className="mb-2 text-md text-white">Name</label>
            <div className="flex gap-2 rounded bg-[#364153] items-center border-[1.5px] border-[#4A5567] hover:border-gray-300">
              <input
                {...register("name", { required: "Name is required" })}
                type="text"
                id="name"
                placeholder="Your Name"
                className="block w-full bg-[#364153] outline-0 p-4 py-3 text-md text-white rounded-sm"
              />
            </div>
            {errors.name && <span className="text-red-500 text-sm mt-1">{errors.name.message}</span>}
          </div>

          <div className="flex flex-col">
            <label htmlFor="email" className="mb-2 text-md text-white">Email</label>
            <div className="flex gap-2 rounded bg-[#364153] items-center border-[1.5px] border-[#4A5567] hover:border-gray-300">
              <input
                {...register("email", { 
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address"
                  }
                })}
                type="email"
                id="email"
                placeholder="Your Email"
                className="block w-full bg-[#364153] outline-0 p-4 py-3 text-md text-white rounded-sm"
              />
            </div>
            {errors.email && <span className="text-red-500 text-sm mt-1">{errors.email.message}</span>}
          </div>

          <div className="flex flex-col">
            <label htmlFor="description" className="mb-2 text-md text-white">Description</label>
            <textarea
              {...register("description", { required: "Description is required" })}
              id="description"
              rows={4}
              className="p-3 rounded-md bg-[#364153] border border-[#4A5567] text-white focus:outline-none focus:border-[#3662E3] placeholder-gray-400"
              placeholder="Describe your needs..."
            />
            {errors.description && <span className="text-red-500 text-sm mt-1">{errors.description.message}</span>}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full mt-6 py-3 rounded-md bg-[#3662E3] hover:bg-[#3662E3]/90 text-white font-semibold transition-all duration-300"
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CustomPlanModal;
